// src/components/steps/Step3History.jsx
//
// Wizard step 3 — the generated outputs of the session, each in a card with its
// metadata, warnings and copy/download actions; the body is drawn by renderEntry
// (renderResultEntry). The warnings are worded in components/warnings/ (WarningList);
// the «Affermazioni nel testo» and «Fonti consultate» panels are components/claims/
// (ClaimsPanel, SourcesPanel), mounted only when the run has `verification` (no legacy
// table for old runs).
//
// The header carries NO action. The card's buttons are all in the body
// (PiecesResultEntry): «Copia tutto» in the «Pronto da pubblicare» block (id
// "all-<entryId>", matched against state.copiedId — every piece in one go) and
// one «Copia» per piece row. There is no download here: the per-format file
// catalogue (publish/downloads.js, DownloadMenu) is offered by «Storico
// Generazioni» (admin/pages/RunDetailPage.jsx) alone.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { displayToneFromParams } from '../../utils/toneMigration.js';
import { translateOptionLabel, resolveToneLabel, MISSING_LABEL } from '../../utils/optionLabels.js';
import { getModeConfig } from '../../constants/modes.js';
import { COLORS, FONT_MONO, RADII } from '../../constants/theme.js';
import WarningList from '../warnings/WarningList.jsx';
import ClaimsPanel from '../claims/ClaimsPanel.jsx';
import SourcesPanel from '../claims/SourcesPanel.jsx';
import { factCheckView } from '../claims/claimsModel.js';

// ── Shared styles ───────────────────────────────────────────────────────────
const labelStyle = (t) => ({
  fontSize: '10px',
  fontFamily: FONT_MONO,
  color: t.textMuted || COLORS.textTertiary,
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
});

const valueStyle = (t) => ({
  fontSize: '13px',
  color: t.text || COLORS.borderStrong,
  fontWeight: 500,
});

// ── Metadata field ──────────────────────────────────────────────────────────
// [FIX-v161] La guardia era `if (!value) return null`, che nascondeva anche
// valori legittimamente "falsy" (uno 0 numerico) e faceva sfalsare la griglia
// quando un campo del riepilogo mancava. Ora si nasconde solo il vero vuoto
// (null / undefined / ''); i campi del riepilogo passano MISSING_LABEL ('—')
// come fallback, così le sei celle ci sono sempre e l'allineamento regge.
// Gli altri call-site (blocco metriche) sono già condizionati a `!= null`,
// quindi il cambio di semantica non li tocca.
const MetaField = ({ label, value, t }) => {
  if (value == null || value === '') return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={labelStyle(t)}>{label}</span>
      <span style={valueStyle(t)}>{value}</span>
    </div>
  );
};

// ── Single history entry ────────────────────────────────────────────────────
const HistoryEntry = ({ entry, renderEntry, color, w, t, tonePresets = [] }) => {
  const { t: tr } = useTranslation('wizard');
  const isCarousel = entry.results?.contentFormat === 'carousel';
  const isNewsletter = entry.results?.contentFormat === 'newsletter';
  const isInstagram = entry.results?.platform === 'instagram';
  const entryColor = isCarousel ? COLORS.accent
    : isNewsletter ? '#0A66C2'
    : isInstagram ? '#E4405F'
    : (entry.modeConfig?.color || color);
  const r = entry.results || {};

  // Il badge di formato che stava nell'header diventa una riga del riepilogo.
  const formatLabel = isCarousel ? tr('step3.carousel')
    : isNewsletter ? tr('step3.newsletter')
    : isInstagram ? tr('step3.instagram')
    : (r.contentFormat || entry.params?.content_format || MISSING_LABEL);

  // Il tono può essere uno slug noto (preset del tenant), uno slug storico o
  // testo libero (tone_hint custom): resolveToneLabel prova la lista servita dal
  // BE, poi l'i18n, e infine restituisce il valore grezzo — così il testo libero
  // resta intatto.
  const rawTone = displayToneFromParams(entry.params);
  const toneLabel = rawTone
    ? resolveToneLabel(tr, rawTone, tonePresets)
    : MISSING_LABEL;

  return (
    <div style={{
      background: t.surface, borderRadius: RADII.card,
      border: `1px solid ${t.border}`, overflow: 'hidden',
    }}>

      {/* ── Section 1: Header ── */}
      {/* [FIX-v161] Via il reviewId (data compatta e illeggibile) e via il badge
          di formato, che ora è una riga del riepilogo insieme a Tipo. Anche la
          data di creazione esce dal riepilogo: la cronologia è già ordinata per
          data e il dettaglio run resta la fonte per i timestamp. */}
      <div style={{
        padding: '16px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
      }}>
        <FileText size={16} color={entryColor} />
        <span style={{
          fontSize: '14px', fontWeight: 700, fontFamily: FONT_MONO,
          color: t.text || COLORS.borderStrong,
        }}>
          {tr('step3.contentReview')}
        </span>
      </div>

      {/* ── Section 1b: Content (always visible, at top) ── */}
      <div style={{ padding: '20px', borderBottom: `1px solid ${t.border}` }}>
        {renderEntry(entry)}
      </div>

      {/* ── Section 1c: Warnings — our words, coloured by severity (components/warnings) ── */}
      {(r.warnings ?? []).length > 0 && (
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.border}` }}>
          <WarningList warnings={r.warnings} t={t} />
        </div>
      )}

      {/* ── Section 2: Riepilogo ── */}
      {/* [FIX-v161] Il riepilogo mostrava slug di wire (drive-traffic,
          cmo-marketing-leader, professional-conversational) invece delle etichette
          viste nel form. Ora passa dallo stesso helper i18n del Riepilogo di input.
          Campi ridotti a quelli chiesti in review; Piattaforma / Canale / Durata /
          Iterazioni rimossi perché non servivano alla rilettura del contenuto. */}
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '12px',
      }}>
        <MetaField label={tr('step3.meta.topic')}  value={r.topic || entry.params?.topic || MISSING_LABEL} t={t} />
        <MetaField label={tr('step3.meta.type')}   value={entry.modeConfig?.name || getModeConfig(entry.mode)?.name || entry.mode || MISSING_LABEL} t={t} />
        <MetaField label={tr('step3.meta.format')} value={formatLabel} t={t} />
        <MetaField label={tr('step3.meta.persona')}   value={translateOptionLabel(tr, 'persona', entry.params?.persona)} t={t} />
        <MetaField label={tr('step3.meta.objective')} value={translateOptionLabel(tr, 'objective', entry.params?.objective)} t={t} />
        <MetaField label={tr('step3.meta.tone')}      value={toneLabel} t={t} />
      </div>

      {/* ── Section 2b: Metriche di autovalidazione ── */}
      {/* [FEAT-v161] Le tre metriche di autovalidazione escono dal blocco dei
          parametri e stanno insieme, come chiesto in review. QA Score usa lo stesso
          fallback qa_score → style_compliance dei renderer di output. */}
      {(() => {
        const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;
        const factCheck = factCheckView(r);
        if (!factCheck.show && r.styleScore == null && qaScore == null) return null;
        return (
          <div style={{
            padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '12px',
          }}>
            {factCheck.show && (
              <MetaField
                label={tr('step3.meta.factCheck')}
                value={factCheck.skipped ? tr('step3.meta.factCheckSkipped') : `${r.factualConfidence}/100`}
                t={t}
              />
            )}
            {r.styleScore != null && (
              <MetaField label={tr('step3.meta.style')} value={`${r.styleScore}/100`} t={t} />
            )}
            {qaScore != null && (
              <MetaField label={tr('step3.meta.qaScore')} value={`${qaScore}/100`} t={t} />
            )}
          </div>
        );
      })()}

      {/* ── Section 4: Affermazioni nel testo + Fonti consultate (results.verification;
          none on old runs, pinterest, youtube) ── */}
      {r.verification && (
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <ClaimsPanel key={r._taskId} results={r} t={t} w={w} />
          <SourcesPanel key={`${r._taskId}-sources`} results={r} t={t} w={w} />
        </div>
      )}

    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const Step3History = ({ state, onRegenerate, onConfirm, onDelete, renderEntry, color, w, t, tonePresets = [] }) => {
  const { t: tr } = useTranslation('wizard');
  const { outputHistory } = state;
  const c = color;

  if (outputHistory.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', background: t.surface, borderRadius: RADII.card, border: `1px solid ${t.border}` }}>
        <div style={{ fontSize: '15px', color: t.textMuted }}>{tr('step3.empty')}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {outputHistory.map(entry => (
        <HistoryEntry
          key={entry.id}
          entry={entry}
          renderEntry={renderEntry}
          color={c}
          w={w}
          t={t}
          tonePresets={tonePresets}
        />
      ))}
    </div>
  );
};

export default Step3History;
